import React from 'react';
import {Checkbox, Select, Button, SpaceBetween} from '@amzn/awsui-components-react/polaris';
import {
    MULTI_ALLOCATION_DATA_SOURCE_ACTIONS
} from 'src/services/multidimensional-allocation/data-sources/MultidimensionalAllocationDataSourcesReducer';
import CONSTANTS from 'src/utils/constants';
import TPEAction from 'src/models/common/TPEAction';
import ServiceCollection from 'src/services/ServiceCollection';
import CommonDataSourceOperations from 'src/services/common/CommonDataSourceOperations';
import Placeholder from 'src/models/tp-allocation/Placeholder';
import CancelEditing from "src/components/calculation-builder/data-sources/CancelEditing";
import {
    MultidimensionalAllocationDataSourceContext
} from "src/services/multidimensional-allocation/data-sources/MultidimensionalAllocationDataSourcesContext";

type ComponentProps = {
    recordBeingEdited: {
        entityId: string,
        description?: string,
        drillDownCOASelected?: string,
        isEditing?: boolean,
        balancePullInProgress?: boolean,
        datasource?: string,
        period?: string,
        customPeriod?: string,
        fxDate?: string,
        fxType?: string,
        allocationSegment?: string,
        allocationSheetId?: string,
        currency?: string,
        errors?: Map<string, string>,
        dataKeyInput?: {
            selectedCompanies: string,
            selectedAccounts: string,
            selectedCOA: Map<string, string>,
        }
    } | undefined,
    sevenSegmentLOV: Map<string, string[]>,
    allRecordDescriptions: string[],
    originalRecordDescription: string,
    coaTypes: string[],
    isSaving: boolean,
    saveButtonLabel: string,
    dispatch: React.Dispatch<TPEAction>,
    services: ServiceCollection,
    onRecordNeedsSaving: (x: string) => void
}

/**
 * MultidimensionalAllocationDataSourceSelectCOA component implementation
 *
 * Renders the COA selection interface with validation and state management
 *
 * @param {ComponentProps} props - Component properties
 * @returns {JSX.Element} The rendered component
 */
export default function MultidimensionalAllocationDataSourceSelectCOA(props: ComponentProps) {
    const {
        recordBeingEdited, sevenSegmentLOV, allRecordDescriptions, coaTypes, dispatch,
        isSaving, saveButtonLabel, services, onRecordNeedsSaving
    } = props;

    const {multidimensionalAllocationDataSourcesState} = React.useContext(MultidimensionalAllocationDataSourceContext);
    const {allocationSheets, customCoaReference = new Map<string, string>()} = multidimensionalAllocationDataSourcesState;

    const {
        dataKeyInput = {selectedCompanies: '', selectedAccounts: '', selectedCOA: new Map<string, string>()},
        isEditing = false,
        allocationSegment = ''
    } = recordBeingEdited || {};

    const {selectedCompanies, selectedAccounts, selectedCOA} = dataKeyInput;

    const coaTypeOptions = coaTypes
        .filter((coaType: string) => (coaType != CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI && coaType != CONSTANTS.COA_SEGMENT_MAPPING.GL_ACCOUNT.UI))
        .map((coaType: string) => ({label: coaType, value: coaType}));


    const isCOASelected = (key: string) => {
        return selectedCOA.has(key);
    }

    const toggleCOA = (key: string) => {
        if (isCOASelected(key)) {
            dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.REMOVE_DATA_KEY_COA_INPUT.withPayload(key));
        } else {
            dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_DATA_KEY_COA_INPUT.withPayload({key: key, value: ''}));
        }
    }

    const saveRecord = () => {
        const errorMap = services.dataSourcesService.validateDataSourceDescription(recordBeingEdited?.description, allRecordDescriptions);

        // Validate allocation sheet selection
        if (!recordBeingEdited?.allocationSheetId) {
            errorMap.set('allocationSheetId', 'Required');
        }

        if (!recordBeingEdited?.allocationSegment) {
            errorMap.set('allocationSegment', 'Segment to allocate on is required');
        }

        // Validate inputs if saved without pulling balance
        if (!recordBeingEdited?.balancePullInProgress) {
            dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.REMOVE_UNUSED_DATA_KEY_COA_INPUTS);

            // This validation will be required for pull balance.
            const inputsErrorMap = CommonDataSourceOperations.validateInputsForPullBalance(recordBeingEdited, false);
            CommonDataSourceOperations.validateCOAInput(CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI, selectedCompanies, sevenSegmentLOV.get(CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI) || [], customCoaReference, inputsErrorMap, new Map<string, Placeholder[]>);
            CommonDataSourceOperations.validateCOAInput(CONSTANTS.COA_SEGMENT_MAPPING.GL_ACCOUNT.UI, selectedAccounts, sevenSegmentLOV.get(CONSTANTS.COA_SEGMENT_MAPPING.GL_ACCOUNT.UI) || [], customCoaReference, inputsErrorMap, new Map<string, Placeholder[]>);
            selectedCOA.forEach((value, key) => CommonDataSourceOperations.validateCOAInput(key, value, sevenSegmentLOV.get(key) || [], customCoaReference, inputsErrorMap, new Map<string, Placeholder[]>));

            if (inputsErrorMap.size > 0) {
                inputsErrorMap.forEach((value: string, key: string) => errorMap.set(key, value));
            }
        }

        if (errorMap.size > 0) {
            dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_ERRORS.withPayload({
                recordID: recordBeingEdited?.entityId,
                value: errorMap
            }));
        } else {
            dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.CLEAR_ERRORS.withPayload({recordID: recordBeingEdited?.entityId}));
            dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_FX_INPUTS);
            onRecordNeedsSaving(new Date().toISOString());
        }
    }

    const handleCOADropdownChange = ({detail}: any) => {
        const selectedCOAType = detail.selectedOption?.value;
        if (selectedCOAType) {

            // Check if current allocation sheet is compatible with new segment
            if (recordBeingEdited?.allocationSheetId && allocationSheets && allocationSheets.length > 0) {
                const currentSheet = allocationSheets.find(sheet => sheet.sheetId === recordBeingEdited.allocationSheetId);
                if (currentSheet && currentSheet.segmentType !== selectedCOAType) {
                    // Clear incompatible allocation sheet selection
                    dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.UPDATE_RECORD_FIELD.withPayload({
                        fieldName: 'allocationSheetId',
                        value: ''
                    }));
                    dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.UPDATE_RECORD_FIELD.withPayload({
                        fieldName: 'allocationSheetVersion',
                        value: undefined
                    }));
                    // Clear any existing allocation sheet errors
                    if (recordBeingEdited?.errors?.has('allocationSheetId')) {
                        const updatedErrors = new Map(recordBeingEdited.errors);
                        updatedErrors.delete('allocationSheetId');
                        dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.SET_ERRORS.withPayload({
                            recordID: recordBeingEdited.entityId,
                            value: updatedErrors.size > 0 ? updatedErrors : undefined
                        }));
                    }
                }
            }

            // Use the existing UPDATE_RECORD_FIELD action
            dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.UPDATE_RECORD_FIELD.withPayload({
                fieldName: 'allocationSegment',
                value: selectedCOAType
            }));

            // Filter allocation sheets based on the selected segment
            dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.FILTER_ALLOCATION_SHEETS_BY_SEGMENT.withPayload(selectedCOAType));

            if (isCOASelected(selectedCOAType)) {
                dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.REMOVE_DATA_KEY_COA_INPUT.withPayload(selectedCOAType));
            }
        }
    }

    return (
        <div className="dataKeyNav">
            <h2>Select COAs to pull GL Balance</h2>
            <div className="coaContainer">
                {coaTypes.filter((coaType: string) => (coaType != CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI && coaType != CONSTANTS.COA_SEGMENT_MAPPING.GL_ACCOUNT.UI))
                    .map((coaType: string) => <div key={coaType} className="coaItemContainer">
                        <Checkbox className={'coaItem ' + coaType.replace(' ', '_') + 'COASelect'}
                                  disabled={!isEditing || (allocationSegment === coaType)}
                                  checked={isCOASelected(coaType)}
                                  onChange={() => toggleCOA(coaType)}>{coaType}</Checkbox>
                    </div>)}
            </div>
            {
                <div className="dataKeyNav">
                    <h2>Segment to allocate on</h2>
                    <Select
                        selectedOption={recordBeingEdited?.allocationSegment ?
                            {label: recordBeingEdited.allocationSegment, value: recordBeingEdited.allocationSegment} :
                            null}
                        onChange={handleCOADropdownChange}
                        options={coaTypeOptions}
                        placeholder="Select segment to add"
                        disabled={!isEditing}
                        invalid={recordBeingEdited?.errors && recordBeingEdited.errors.has('allocationSegment')}
                    />
                </div>
            }
            {isEditing &&
                <div>
                    <SpaceBetween className="actionButtonsContainer" direction="horizontal" size="xs">
                        <CancelEditing
                            onCancelPostProcess={() => dispatch(MULTI_ALLOCATION_DATA_SOURCE_ACTIONS.CANCEL_EDITING)}/>
                        <Button className="smallPrimaryButton saveDataSourceBtn" loading={isSaving}
                                onClick={saveRecord}>{isSaving ? "Saving..." : saveButtonLabel}</Button>
                    </SpaceBetween>
                </div>
            }
        </div>
    )
}
